import { useMutation } from '@apollo/client/react';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { Switch } from '@optools/components/ui/switch';
import { SET_EVI_STATUS } from '@optools/services/operator-gateway/btc/mutations';
import {
  MediaPipeline,
  PipelineEviStatus,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { isEVIDisabled } from '../../../utils/evi';

export type EVIStatusSwitchProps = Pick<MediaPipeline, 'eviStatus' | 'status'> & {
  mediaId: string;
};

export function EVIStatusSwitch({ eviStatus, mediaId, status }: EVIStatusSwitchProps) {
  const [currentEviStatus, setCurrentEviStatus] = useState<PipelineEviStatus>(eviStatus);

  useEffect(() => {
    setCurrentEviStatus(eviStatus);
  }, [eviStatus]);

  const [setEviStatusMutation, { loading }] = useMutation(SET_EVI_STATUS, {
    onCompleted: (data) => {
      const { reason, success } = data.setEviStatus;
      if (!success) {
        setCurrentEviStatus((current) =>
          current === PipelineEviStatus.ENABLED
            ? PipelineEviStatus.DISABLED
            : PipelineEviStatus.ENABLED,
        );

        toast.error('EVI Status Update Failed', {
          description: (
            <>
              <p className="mb-1">{reason}</p>
              <p>Media ID: {mediaId}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
    onError: (error) => {
      setCurrentEviStatus((current) =>
        current === PipelineEviStatus.ENABLED
          ? PipelineEviStatus.DISABLED
          : PipelineEviStatus.ENABLED,
      );

      displayApolloError({
        error,
        options: { description: `Media ID: ${mediaId}` },
        title: `EVI Status Update Failed`,
      });
    },
  });

  const handleToggleEVIStatus = (checked: boolean) => {
    const status = checked ? PipelineEviStatus.ENABLED : PipelineEviStatus.DISABLED;
    setCurrentEviStatus(status);
    setEviStatusMutation({ variables: { mediaId, status } });
  };

  const disabled = loading || isEVIDisabled(status);

  return (
    <div className="flex flex-col items-start gap-2">
      <span className="font-bold">Status</span>

      <div className="flex flex-1 items-center gap-2 text-xs has-[button:disabled]:opacity-50">
        <span>Disabled</span>
        <Switch
          aria-label="EVI Status"
          checked={currentEviStatus === PipelineEviStatus.ENABLED}
          className="peer"
          disabled={disabled}
          size="sm"
          onCheckedChange={handleToggleEVIStatus}
        />
        <span>Enabled</span>
      </div>
    </div>
  );
}
